/**
 * Thin fetch wrapper for the markaii-api. All requests go through the
 * nginx /api/* reverse proxy (see frontend/nginx.conf).
 *
 * Reads the access token from localStorage on every call so a stale
 * closure can't outlive a logout. On 401 it clears local auth state
 * and reloads — the ProtectedRoute then bounces to /login.
 */

const API_BASE = '/api';
const STORAGE_KEY = 'markaii.auth';

function readToken() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw)?.accessToken || null;
  } catch {
    return null;
  }
}

function clearAuthAndReload() {
  localStorage.removeItem(STORAGE_KEY);
  window.location.assign('/login');
}

async function request(method, path, { body, signal } = {}) {
  const token = readToken();
  const headers = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (res.status === 401) {
    clearAuthAndReload();
    const err = new Error('unauthorized');
    err.status = 401;
    throw err;
  }

  if (res.status === 204) return null;

  if (!res.ok) {
    let parsed = null;
    try { parsed = await res.json(); } catch (_) { /* ignore */ }
    const err = new Error(`API ${res.status}: ${parsed?.error || res.statusText}`);
    err.status = res.status;
    err.body = parsed;

    // ADR-0015: surface quota / rate-limit / trial-expired events on a
    // shared event channel. The QuotaBanner component listens for these
    // and renders an inline upgrade CTA. Components doing the original
    // call still get the throw, so their local error UI keeps working.
    if (res.status === 429 || res.status === 402) {
      try {
        window.dispatchEvent(new CustomEvent('markaii:quota-event', {
          detail: { status: res.status, body: parsed, path },
        }));
      } catch (_e) { /* SSR-safe */ }
    }
    throw err;
  }
  return res.json();
}

export const apiGet    = (path, opts) => request('GET',    path, opts);
export const apiPost   = (path, body, opts) => request('POST',   path, { ...opts, body });
export const apiPatch  = (path, body, opts) => request('PATCH',  path, { ...opts, body });
export const apiDelete = (path, opts) => request('DELETE', path, opts);
export { readToken };
